import React from "react";
import { useFela } from "react-fela";

import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Image } from "@/components/Image";
import { Heading } from "@/components/Heading";
import { IHowItWorks } from "./types";

type IProps = IHowItWorks & JSX.IntrinsicElements["div"];

export const HowItWorks: React.FC<IProps> = ({
  title,
  steps,
  subtitle,
  wrapperStyles,
  ...rest
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();

  return (
    <Grid
      id="DashboardSection"
      customStyle={{
        position: "relative",
        flexDirection: "column",
        alignItems: "center",
      }}
      wrapperStyle={{
        background: "#F8F9FE",
        ...wrapperStyles,
      }}
      {...rest}
    >
      <Heading
        as="h2"
        customStyle={{
          fontSize: 28,
          letterSpacing: "-0.01rem",
          [bp.FROM_DESKTOP]: {
            paddingBottom: 36,
            fontSize: 48,
            lineHeight: "60px",
          },
        }}
      >
        {title}
      </Heading>
      <ol
        className={css({
          width: "100%",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          listStyle: "none",
          [bp.FROM_DESKTOP]: {
            alignItems: "flex-start",
          },
        })}
      >
        {steps.map(({ id, text, image, rightSidedImage }, index) => (
          <li key={id} className={css({ width: "100%" })}>
            <div
              className={css({
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "24px 0",
                [bp.FROM_TABLET]: {
                  flexDirection: rightSidedImage ? "row" : "row-reverse",
                },
              })}
            >
              <p
                className={css({
                  paddingBottom: 20,
                  fontSize: 20,
                  color: colors.DARK_GREY,
                  fontWeight: "bold",
                  [bp.FROM_TABLET]: {
                    maxWidth: 440,
                    paddingBottom: 0,
                    padding: "0px 20px",
                  },
                  [bp.FROM_DESKTOP]: {
                    fontSize: 28,
                    lineHeight: "36px",
                  },
                })}
              >
                <span
                  className={css({ [bp.FROM_TABLET]: { display: "none" } })}
                >
                  {index + 1}.{" "}
                </span>
                {text}
              </p>
              <Image
                customStyle={{
                  maxWidth: 600,
                  width: "100%",
                  margin: "0 auto",
                  border: index === 1 ? "none" : "1px solid #E5EAF4",
                  filter: "drop-shadow(20px 20px 80px rgba(16, 5, 48, 0.08))",
                  borderRadius: 12,
                }}
                src={image}
                alt="Dashboard Screen"
              />
            </div>
            {index !== steps.length - 1 && (
              <img
                src="/svgs/dashed-line.svg"
                alt="Dashed line"
                className={css({
                  maxWidth: "100%",
                  display: "none",
                  margin: "0 auto",
                  transform: rightSidedImage ? "" : "scaleX(-1)",
                  [bp.FROM_TABLET]: {
                    display: "block",
                  },
                })}
              />
            )}
          </li>
        ))}
      </ol>
    </Grid>
  );
};
